/**
 * Upload Manager
 *
 * One app-wide owner for every folder upload in flight.
 *
 * Before this, each `/upload` mount owned its own upload state and the request
 * chain survived navigation only as a leaked promise: nothing tracked it, a
 * second folder could not queue behind it, and the deal-room spinner was a
 * single shared boolean that the first upload to finish switched off for all
 * the others. The manager makes a job a first-class object with an id, a
 * project, a status and a progress of its own, held above the router so it
 * outlives the page that started it.
 *
 * Concurrency has two different rules on purpose:
 *   - WITHIN a job, batches stay strictly sequential. `is_last_batch` is what
 *     fires the backend Cabinet ingest, so nothing may overtake the batch
 *     before it.
 *   - ACROSS jobs, up to `MAX_CONCURRENT_JOBS` run at once and the rest wait
 *     FIFO. Starting a new job never touches a running one. Two jobs for the
 *     SAME project never run together: the later one waits its turn.
 */

import React, {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from "react";

import {
	UPLOAD_STATUS_EVENT,
	UploadStatusEventDetail,
	USER_FILES_UPDATED_EVENT,
} from "../constants/events";
import { runDealRoomUploadJob } from "../utils/dealRoomUpload";
import { runUploadJob, UploadResponse } from "../utils/uploadEngine";

/**
 * Jobs allowed to be actively uploading at the same time. 3 (DOP-248) keeps a
 * connection or two free for status polls and chat SSE under the browser's
 * ~6-per-origin HTTP/1.1 budget.
 */
export const MAX_CONCURRENT_JOBS = 3;

export type UploadJobStatus =
	| "queued"
	| "uploading"
	| "done"
	| "partial"
	| "failed";

export interface UploadJobResult {
	/** Files the server confirmed it stored. */
	uploaded: number;
	/** Relative paths the server rejected, or that a failed batch carried. */
	failed: string[];
	/** Relative paths in batches the loop never got to. */
	notAttempted: string[];
	/** Why each failed file failed, in words the user can act on. */
	reasons?: Array<{ filename: string; error: string }>;
}

export interface UploadJob {
	id: string;
	projectId: string;
	projectName: string;
	/** The deal-room folder the files land in — the spinner's row anchor. */
	folderId: number;
	reportingPeriod: string;
	files: File[];
	/** 0-100 for THIS job only. */
	progress: number;
	status: UploadJobStatus;
	result?: UploadJobResult;
	/**
	 * Id of the job that was enqueued to repair this one. Set the moment a Retry
	 * is accepted, so the tray can show this job as already-retried instead of
	 * offering a second Retry beside a repair that is already running.
	 */
	retriedByJobId?: string;
	/**
	 * Ids of the jobs THIS job is a repair for — the direct source first, then
	 * its own ancestors. Carried on the repair itself rather than looked up
	 * through back-pointers so the chain survives an intermediate job being
	 * dismissed from the tray.
	 */
	repairsJobIds: string[];
	/**
	 * Id of the repair that finished this job's work successfully. Stamped on
	 * THIS job at the successor's settle time, so it survives that successor
	 * being dismissed from the list.
	 *
	 * A resolved job is permanently non-retryable. Its `files` are the browser's
	 * handles on what the user picked back THEN; re-sending them with
	 * `overwrite_existing=true` would roll same-path files back over whatever a
	 * later upload corrected. That is data loss, so the row keeps its counts for
	 * the record but loses its Retry.
	 */
	resolvedByJobId?: string;
	/** Set when the job could not run at all (as opposed to a partial upload). */
	error?: string;
	startedAt: number;
}

export interface EnqueueUploadInput {
	projectId: string;
	projectName: string;
	folderId: number;
	reportingPeriod: string;
	files: File[];
}

export interface UploadManagerContextValue {
	jobs: UploadJob[];
	/** Adds a job and starts it as soon as a slot is free. Returns its id. */
	enqueueUpload: (input: EnqueueUploadInput) => string;
	/** Re-runs a finished job's file list as a NEW job against the same project. */
	retryUpload: (jobId: string) => string | null;
	/** Removes a finished job from the tray. Running jobs are left alone. */
	dismissJob: (jobId: string) => void;
	/** Removes every finished job from the tray. */
	clearFinishedJobs: () => void;
	/** Projects with an upload queued or running. */
	uploadingProjectIds: Set<string>;
	/** Deal-room folders with an upload queued or running. */
	uploadingFolderIds: Set<number>;
}

const UploadManagerContext = createContext<UploadManagerContextValue | undefined>(
	undefined
);

export const useUploadManager = (): UploadManagerContextValue => {
	const context = useContext(UploadManagerContext);
	if (!context) {
		throw new Error(
			"useUploadManager must be used within an UploadManagerProvider"
		);
	}
	return context;
};

/**
 * The manager, or `null` outside a provider (a component rendered alone in a
 * unit test). For callers that can do without it rather than crash.
 */
export const useOptionalUploadManager = (): UploadManagerContextValue | null =>
	useContext(UploadManagerContext) ?? null;

/** True while the job is a claim on the deal room: not finished yet. */
export const isJobActive = (job: UploadJob): boolean =>
	job.status === "queued" || job.status === "uploading";

/**
 * True when some job is already queued or uploading against this project.
 *
 * One project may only have one upload in flight. A second one would take a
 * concurrency slot, re-send the same folder at the same cost, and fire a second
 * final-batch ingest over the project the first one is still filling — and the
 * tray would show a stale failure next to a running repair. The server being
 * idempotent makes the duplicate harmless, not free.
 *
 * `retryUpload` and the tray's Retry button share this predicate deliberately:
 * the button must never be offered in a state where pressing it does nothing.
 */
export const hasActiveJobForProject = (
	jobs: UploadJob[],
	projectId: string
): boolean => jobs.some((job) => isJobActive(job) && job.projectId === projectId);

/**
 * The ONE rule for whether a job may be retried. The manager enforces it and
 * the tray renders from it, so the button is never offered where the call would
 * be refused.
 *
 * A job is retryable only when all of these hold:
 *   - it actually failed (a clean `done` has nothing to repair);
 *   - it is finished (a running job is not a repair candidate);
 *   - no later upload already finished its work — see `resolvedByJobId`;
 *   - nothing else is in flight for the same deal room.
 */
export const canRetryJob = (jobs: UploadJob[], job: UploadJob): boolean => {
	if (job.status !== "partial" && job.status !== "failed") return false;
	if (isJobActive(job)) return false;
	if (job.resolvedByJobId) return false;
	return !hasActiveJobForProject(jobs, job.projectId);
};

let jobSequence = 0;
const nextJobId = (): string => {
	jobSequence += 1;
	return `upload-${jobSequence}-${Math.random().toString(36).slice(2, 8)}`;
};

interface UploadManagerProviderProps {
	children: ReactNode;
	/** Injection seam for tests; defaults to the real XHR batch loop. */
	runJob?: typeof runUploadJob;
}

export const UploadManagerProvider: React.FC<UploadManagerProviderProps> = ({
	children,
	runJob = runDealRoomUploadJob,
}) => {
	const [jobs, setJobs] = useState<UploadJob[]>([]);

	// The scheduler reads and writes the job list outside React's render cycle
	// (a job finishing has to pick the next queued one immediately, not one
	// render later), so the list is mirrored in a ref and every mutation goes
	// through `updateJobs`. The provider is the only writer, so the mirror
	// cannot drift.
	const jobsRef = useRef<UploadJob[]>([]);
	/** Ids currently executing. Authoritative for how many slots are taken. */
	const activeIdsRef = useRef<Set<string>>(new Set());

	const updateJobs = useCallback(
		(mutate: (current: UploadJob[]) => UploadJob[]) => {
			const next = mutate(jobsRef.current);
			jobsRef.current = next;
			setJobs(next);
		},
		[]
	);

	const patchJob = useCallback(
		(jobId: string, patch: Partial<UploadJob>) => {
			updateJobs((current) =>
				current.map((job) => (job.id === jobId ? { ...job, ...patch } : job))
			);
		},
		[updateJobs]
	);

	const announceStatus = useCallback((job: UploadJob, isUploading: boolean) => {
		if (typeof window === "undefined") return;
		// The Sidebar reads the manager through context; this event stays for
		// anything outside the provider that still wants to know. It carries the
		// project it is about — a bare boolean is what made one upload finishing
		// clear the spinner for every other upload still running.
		const detail: UploadStatusEventDetail = {
			jobId: job.id,
			projectId: job.projectId,
			projectName: job.projectName,
			folderId: job.folderId,
			isUploading,
		};
		window.dispatchEvent(new CustomEvent(UPLOAD_STATUS_EVENT, { detail }));
	}, []);

	/**
	 * Classify a finished run. A stopped loop with files already persisted is
	 * `partial`, not `failed`: those files are really there, and the repair is a
	 * retry of the same list.
	 */
	const settle = useCallback(
		(jobId: string, response: UploadResponse) => {
			const aborted = response.aborted;
			const result: UploadJobResult = {
				uploaded: response.total_uploaded,
				failed: aborted ? aborted.failed : response.errors.map((e) => e.filename),
				notAttempted: aborted ? aborted.notAttempted : [],
				reasons: response.errors,
			};
			const status: UploadJobStatus = !aborted
				? "done"
				: response.total_uploaded > 0
					? "partial"
					: "failed";

			updateJobs((current) => {
				const settling = current.find((job) => job.id === jobId);
				// Only a FULLY clean run resolves what it was repairing. A partial
				// repair leaves files still missing, so its source stays retryable.
				const resolvedIds =
					status === "done" && settling
						? new Set(settling.repairsJobIds)
						: new Set<string>();
				return current.map((job) => {
					if (job.id === jobId) return { ...job, status, result, progress: 100 };
					// Stamped on the ancestor ITSELF, not inferred from the successor
					// still being in the list — the successor is dismissable, and the
					// old file handles must not become re-sendable when it goes.
					if (resolvedIds.has(job.id))
						return { ...job, resolvedByJobId: jobId };
					return job;
				});
			});

			// Anything that landed is visible in the deal room, even on a partial.
			if (response.total_uploaded > 0 && typeof window !== "undefined") {
				window.dispatchEvent(
					new CustomEvent(USER_FILES_UPDATED_EVENT, {
						detail: { files: response.uploaded_files },
					})
				);
			}
		},
		[updateJobs]
	);

	// `pump` and `startJob` are mutually recursive (a finished job pumps the
	// queue). A ref breaks the definition cycle without making either callback
	// unstable.
	const pumpRef = useRef<() => void>(() => {});

	const startJob = useCallback(
		async (job: UploadJob) => {
			activeIdsRef.current.add(job.id);
			patchJob(job.id, { status: "uploading", progress: 0 });
			announceStatus(job, true);

			try {
				const response = await runJob({
					files: job.files,
					projectId: job.projectId,
					folderId: job.folderId,
					reportingPeriod: job.reportingPeriod,
					onProgress: (progress) => {
						// A late tick from a job the user already dismissed must not
						// resurrect it; `patchJob` is a no-op for an absent id.
						patchJob(job.id, { progress });
					},
				});
				settle(job.id, response);
			} catch (error: any) {
				// The engine turns per-batch failures into an `aborted` result, so
				// reaching here means the run could not happen at all.
				patchJob(job.id, {
					status: "failed",
					progress: 100,
					error: error?.message || "Upload failed",
					result: {
						uploaded: 0,
						failed: [],
						notAttempted: job.files.map((file) => file.name),
					},
				});
			} finally {
				activeIdsRef.current.delete(job.id);
				announceStatus(job, false);
				pumpRef.current();
			}
		},
		[announceStatus, patchJob, runJob, settle]
	);

	/**
	 * Start queued jobs, oldest first, until the concurrency slots are full.
	 *
	 * At most ONE job per project runs at a time (DOP-608). Every batch sends
	 * `overwrite_existing=true`, so two jobs into the same deal carrying the
	 * same path would race and whichever committed last would win — silently
	 * replacing the file the analyst added second. Queued behind each other,
	 * they land in the order they were added.
	 */
	const pump = useCallback(() => {
		let free = MAX_CONCURRENT_JOBS - activeIdsRef.current.size;
		if (free <= 0) return;
		const busyProjects = new Set(
			jobsRef.current
				.filter((job) => activeIdsRef.current.has(job.id))
				.map((job) => job.projectId)
		);
		for (const job of jobsRef.current) {
			if (free <= 0) break;
			if (job.status !== "queued") continue;
			if (activeIdsRef.current.has(job.id)) continue;
			if (busyProjects.has(job.projectId)) continue;
			free -= 1;
			busyProjects.add(job.projectId);
			void startJob(job);
		}
	}, [startJob]);

	pumpRef.current = pump;

	/**
	 * Append a job and give the scheduler a chance to start it. `repairsJobIds`
	 * is the lineage a retry carries; a first-time upload repairs nothing.
	 */
	const addJob = useCallback(
		(input: EnqueueUploadInput, repairsJobIds: string[]): string => {
			const job: UploadJob = {
				id: nextJobId(),
				projectId: input.projectId,
				projectName: input.projectName,
				folderId: input.folderId,
				reportingPeriod: input.reportingPeriod,
				files: input.files,
				progress: 0,
				status: "queued",
				repairsJobIds,
				startedAt: Date.now(),
			};
			updateJobs((current) => [...current, job]);
			pump();
			return job.id;
		},
		[pump, updateJobs]
	);

	const enqueueUpload = useCallback(
		(input: EnqueueUploadInput): string => addJob(input, []),
		[addJob]
	);

	const retryUpload = useCallback(
		(jobId: string): string | null => {
			const job = jobsRef.current.find((candidate) => candidate.id === jobId);
			if (!job) return null;
			// One rule, enforced here and rendered by the tray. It refuses a job
			// that is still running, one whose work a later upload already
			// finished, and — the round-2 case — any job whose deal room already
			// has an upload in flight, which is what a double-click produces.
			if (!canRetryJob(jobsRef.current, job)) return null;
			// A retry is a NEW run of the same file list against the same project,
			// not a resumption: the server overwrites by relative path and the
			// Cabinet cache is content-addressed, so re-sending what already landed
			// is free and creates no duplicates.
			const successorId = addJob(
				{
					projectId: job.projectId,
					projectName: job.projectName,
					folderId: job.folderId,
					reportingPeriod: job.reportingPeriod,
					files: job.files,
				},
				// The repair inherits its source's lineage, so one clean run resolves
				// the whole chain behind it — A → B → C means C's success retires A
				// as well as B, even if B has since been dismissed.
				[job.id, ...job.repairsJobIds]
			);
			// Point the source at its repair so the tray stops offering a Retry it
			// would refuse. If the successor fails the source becomes retryable
			// again — the successor is no longer active, and its own Retry works too.
			patchJob(job.id, { retriedByJobId: successorId });
			return successorId;
		},
		[addJob, patchJob]
	);

	const dismissJob = useCallback(
		(jobId: string) => {
			updateJobs((current) =>
				current.filter((job) => job.id !== jobId || isJobActive(job))
			);
		},
		[updateJobs]
	);

	const clearFinishedJobs = useCallback(() => {
		updateJobs((current) => current.filter(isJobActive));
	}, [updateJobs]);

	const uploadingProjectIds = useMemo(
		() => new Set(jobs.filter(isJobActive).map((job) => job.projectId)),
		[jobs]
	);

	const uploadingFolderIds = useMemo(
		() => new Set(jobs.filter(isJobActive).map((job) => job.folderId)),
		[jobs]
	);

	const value = useMemo<UploadManagerContextValue>(
		() => ({
			jobs,
			enqueueUpload,
			retryUpload,
			dismissJob,
			clearFinishedJobs,
			uploadingProjectIds,
			uploadingFolderIds,
		}),
		[
			jobs,
			enqueueUpload,
			retryUpload,
			dismissJob,
			clearFinishedJobs,
			uploadingProjectIds,
			uploadingFolderIds,
		]
	);

	return (
		<UploadManagerContext.Provider value={value}>
			{children}
		</UploadManagerContext.Provider>
	);
};
